/**
 * Weak / predictable password detection.
 *
 * Dictionary: lowercase union of SecLists
 * `Passwords/Common-Credentials/xato-net-10-million-passwords-10000.txt` and a small set of
 * product-specific tokens. Source and license: https://github.com/danielmiessler/SecLists (MIT).
 *
 * Entries shorter than {@link MIN_PASSWORD_LENGTH} are not treated as dictionary matches alone:
 * policy rejects them on length first. For passwords that meet the length rule, we still match
 * exact list entries and common prefixes (length ≥ {@link MIN_WEAK_PREFIX_LENGTH}) so bases like
 * `password…` plus padding remain blocked.
 */

import {
  MIN_PASSWORD_LENGTH,
  MIN_WEAK_HOMOGENEOUS_RUN,
  MIN_WEAK_PREFIX_LENGTH,
  MIN_WEAK_REPEAT_UNIT,
  MIN_WEAK_SEQUENCE_RUN,
  PASSWORD_ORG_NAME_YEAR_PATTERN,
  PASSWORD_PREDICTABLE_HIRE_PATTERN,
  PASSWORD_SEASON_QUARTER_YEAR_PATTERN,
  PASSWORD_SUPPLEMENTAL_WEAK_TOKENS,
} from '../constants/password-constants.ts';
import seclistsTopPasswords from '../data/seclists-xato-top10000.ts';

function buildCommonWeakPasswordSet(): Set<string> {
  const set = new Set<string>();
  for (const entry of seclistsTopPasswords) {
    const t = String(entry).trim().toLowerCase();
    if (t) set.add(t);
  }
  for (const w of PASSWORD_SUPPLEMENTAL_WEAK_TOKENS) {
    set.add(w);
  }
  return set;
}

export const COMMON_WEAK_PASSWORDS = buildCommonWeakPasswordSet();

/**
 * Map common leetspeak to ASCII for detecting "password" and similar dictionary strings.
 */
function applySimpleLeetSubstitutions(s: string): string {
  return s
    .replace(/@/g, 'a')
    .replace(/0/g, 'o')
    .replace(/3/g, 'e')
    .replace(/4/g, 'a')
    .replace(/5/g, 's')
    .replace(/7/g, 't')
    .replace(/\$/g, 's');
}

function maxSequentialRunInChunk(chunk: string, delta: 1 | -1): number {
  if (chunk.length < MIN_WEAK_SEQUENCE_RUN) return 0;
  let maxRun = 1;
  let run = 1;
  for (let k = 1; k < chunk.length; k++) {
    const d = chunk[k].charCodeAt(0) - chunk[k - 1].charCodeAt(0);
    if (d === delta) {
      run++;
      maxRun = Math.max(maxRun, run);
    } else {
      run = 1;
    }
  }
  return maxRun;
}

/**
 * Long ascending/descending digit sequences (e.g. 1234567890123456).
 */
function hasLongDigitSequence(s: string): boolean {
  const chunks = s.match(/\d+/g);
  if (!chunks) return false;
  for (const chunk of chunks) {
    if (maxSequentialRunInChunk(chunk, 1) >= MIN_WEAK_SEQUENCE_RUN) return true;
    if (maxSequentialRunInChunk(chunk, -1) >= MIN_WEAK_SEQUENCE_RUN) return true;
  }
  return false;
}

/**
 * Long ascending/descending alphabetic sequences (e.g. abcdefghijklmnop).
 */
function hasLongLetterSequence(s: string): boolean {
  const lower = s.toLowerCase();
  const chunks = lower.match(/[a-z]+/g);
  if (!chunks) return false;
  for (const chunk of chunks) {
    if (maxSequentialRunInChunk(chunk, 1) >= MIN_WEAK_SEQUENCE_RUN) return true;
    if (maxSequentialRunInChunk(chunk, -1) >= MIN_WEAK_SEQUENCE_RUN) return true;
  }
  return false;
}

/** Same character repeated many times in a row. */
function hasLongHomogeneousRun(s: string): boolean {
  return new RegExp(`(.)\\1{${MIN_WEAK_HOMOGENEOUS_RUN - 1},}`).test(s);
}

/**
 * Adjacent duplicate phrase: iloveyouiloveyou, monkeymonkey123, etc.
 */
function hasRepeatedPhraseBlock(s: string): boolean {
  return new RegExp(`(.{${MIN_WEAK_REPEAT_UNIT},})\\1`).test(s);
}

/**
 * "password" / leetspeak (p@ssw0rd) and doubled patterns (passwordpassword1!).
 */
function hasPasswordVariantOrDoubling(leetNormalized: string): boolean {
  if (leetNormalized.includes('password')) {
    return true;
  }
  const lettersOnly = leetNormalized.replace(/[^a-z]/g, '');
  return lettersOnly.includes('passwordpassword');
}

/** Predictable onboarding / contractor patterns (e.g. Sym_newhireOEIE). */
function hasPredictableHirePattern(s: string): boolean {
  return PASSWORD_PREDICTABLE_HIRE_PATTERN.test(s);
}

/** Season / quarter + year patterns (e.g. Winter2022!, Q32024). */
function hasSeasonOrQuarterWithYear(s: string): boolean {
  return PASSWORD_SEASON_QUARTER_YEAR_PATTERN.test(s);
}

/**
 * Product / company name + calendar year (Insightia2026!, Diligent2025!, etc.).
 */
function hasOrgNameWithYear(s: string): boolean {
  const t = s.toLowerCase();
  return PASSWORD_ORG_NAME_YEAR_PATTERN.test(t);
}

function matchesPredictablePatterns(normalized: string): boolean {
  const lower = normalized.toLowerCase();
  const leet = applySimpleLeetSubstitutions(lower);
  return (
    hasLongDigitSequence(lower) ||
    hasLongLetterSequence(lower) ||
    hasLongHomogeneousRun(lower) ||
    hasRepeatedPhraseBlock(lower) ||
    hasPasswordVariantOrDoubling(leet) ||
    hasPredictableHirePattern(lower) ||
    hasSeasonOrQuarterWithYear(lower) ||
    hasOrgNameWithYear(lower)
  );
}

function matchesPublicWeakDictionary(normalized: string): boolean {
  if (COMMON_WEAK_PASSWORDS.has(normalized)) {
    return true;
  }
  for (const weak of COMMON_WEAK_PASSWORDS) {
    if (weak.length >= MIN_WEAK_PREFIX_LENGTH && normalized.startsWith(weak)) {
      return true;
    }
  }
  return false;
}

export function isCommonWeakPassword(password: string): boolean {
  if (!password || typeof password !== 'string') return true;
  const normalized = password.trim().toLowerCase();
  if (normalized.length === 0) return true;
  if (matchesPredictablePatterns(normalized)) {
    return true;
  }
  if (normalized.length >= MIN_PASSWORD_LENGTH && matchesPublicWeakDictionary(normalized)) {
    return true;
  }
  return false;
}
